/**
 * EventDetailPage — routed single-event view (`/events/:model/:eventId`), the event twin of
 * `/alerts/:id`.
 *
 * The report tables open an event in a modal, because a table is a position an operator is
 * working down. This page is for the times the record has to outlive that position: a link
 * pasted into a ticket, a bookmark, a second monitor. It renders the SAME body the modal does
 * (EventDetailBody), so the two cannot drift into different records.
 *
 * The event lives on a node, so the page needs one selected: without it there is nothing to ask.
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { DEFAULT_REPORT_SLUG, PageTitle, reportModelBySlug } from '@trinetra/shared';
import { useNode } from '../contexts/NodeContext';
import { getNodeModelEvent, getNodeCameras } from '../services/api.service';
import { EventDetailBody, EventDetailTitle } from './AlertInvestigationPage';

export default function EventDetailPage() {
  const { model: slug, eventId = '' } = useParams();
  const navigate = useNavigate();
  const { selectedNode } = useNode();
  const nodeId = selectedNode?.nodeId || '';
  const entry = reportModelBySlug(slug);
  const model = entry?.key || '';

  const [event, setEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [cameraNameMap, setCameraNameMap] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!nodeId || !model || !eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Resolves either the AI `event_id` or the DB row id — the modal's "Full page" button
      // prefers the former, and older links carry the latter.
      setEvent(await getNodeModelEvent(nodeId, model, eventId));
    } catch (e) {
      console.error('Failed to load event:', e);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [nodeId, model, eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId)
      .then((cams: any) => {
        const list: any[] = cams?.data || cams || [];
        setCameraNameMap(Object.fromEntries(list.map((c: any) => [c.id, c.name || c.id])));
      })
      .catch(() => {});
  }, [nodeId]);

  // An unknown model in the URL is a dead link, not an empty record — send the reader to a
  // report that exists rather than to a page that can only ever say "not found".
  if (!entry) return <Navigate to={`/reports/${DEFAULT_REPORT_SLUG}`} replace />;

  return (
    <div className="space-y-3">
      <PageTitle title={event ? <EventDetailTitle event={event} model={model} /> : entry.label}>
        <button
          data-tour="event-back"
          onClick={() => navigate(`/reports/${entry.slug}`)}
          className="flex h-8 shrink-0 items-center gap-1 rounded-lg border border-border px-2.5 text-[12px] text-foreground hover:bg-muted transition-colors"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" /> {entry.label}
        </button>
        <button
          data-tour="event-refresh"
          onClick={load}
          title="Refresh"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </PageTitle>

      <div data-tour="event-record" className="rounded-xl border border-border bg-card shadow-card">
        {!nodeId ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Select a site to load this event.
          </div>
        ) : loading ? (
          <div className="p-6"><div className="h-64 animate-pulse rounded-lg bg-muted" /></div>
        ) : !event ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            That event is no longer available — it may have been deleted, passed its retention
            window, or the node is unreachable.
          </div>
        ) : (
          <EventDetailBody
            event={event}
            model={model}
            nodeId={nodeId}
            cameraNameMap={cameraNameMap}
            personName={(e: any) => e?.matched_person_name || null}
          />
        )}
      </div>
    </div>
  );
}
